import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const WishlistContext = createContext(undefined);

export function WishlistProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("wishlist_items");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Sync items to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem("wishlist_items", JSON.stringify(items));
    } catch {}
  }, [items]);

  // Listen for localStorage changes (from other tabs or direct updates)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "wishlist_items") {
        try {
          const raw = e.newValue;
          const newItems = raw ? JSON.parse(raw) : [];
          setItems(newItems);
        } catch {
          // Ignore parse errors
        }
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom events (from same window)
    const handleCustomStorageChange = () => {
      try {
        const raw = localStorage.getItem("wishlist_items");
        const newItems = raw ? JSON.parse(raw) : [];
        setItems(newItems);
      } catch {
        // Ignore parse errors
      }
    };

    window.addEventListener("wishlist-updated", handleCustomStorageChange);

    // Poll localStorage periodically to catch direct updates
    const interval = setInterval(() => {
      try {
        const raw = localStorage.getItem("wishlist_items");
        const newItems = raw ? JSON.parse(raw) : [];
        const currentItemsStr = JSON.stringify(items);
        const newItemsStr = JSON.stringify(newItems);
        if (currentItemsStr !== newItemsStr) {
          setItems(newItems);
        }
      } catch {
        // Ignore errors
      }
    }, 500); // Check every 500ms

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("wishlist-updated", handleCustomStorageChange);
      clearInterval(interval);
    };
  }, [items]);

  const addItem = (item) => {
    if (!item || !item.id) return;
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.id === String(item.id));
      if (idx >= 0) {
        // Already in wishlist, update it
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          ...item,
          is_favorite: true,
        };
        return next;
      }
      return [
        ...prev,
        {
          id: String(item.id),
          name: item.title || item.name || "",
          title: item.title || item.name || "",
          price: item.price ?? null,
          list_price: item.list_price ?? null,
          image:
            item.image ||
            item.image_url_1920 ||
            item.image_url ||
            (item.id
              ? `https://erp-elsalab.odoo.com/web/image/${item.id}`
              : ""),
          image_url_1920: item.image_url_1920 || null,
          image_url: item.image_url || null,
          availability: item.availability || "In stock",
          brand: item.brand || item.att_model_name || "",
          colors: item.colors || (item.att_color ? [item.att_color] : []),
          sizes:
            item.sizes ||
            (item.att_size && item.att_size !== "-" ? [item.att_size] : []),
          att_size: item.att_size || null,
          att_color: item.att_color || null,
          att_specifications: item.att_specifications || null,
          description: item.description || null,
          categ_name: item.categ_name || null,
          current_offer: item.current_offer || null,
          package_quantity: item.package_quantity || 1,
          stock: item.stock || [],
          category: item.category || null,
          is_favorite: true,
        },
      ];
    });
  };

  const removeItem = (id) =>
    setItems((prev) => prev.filter((x) => x.id !== String(id)));

  const toggleItem = (item, isFavorite) => {
    if (isFavorite) {
      addItem(item);
    } else {
      removeItem(item.id);
    }
  };

  const isInWishlist = (id) => items.some((x) => x.id === String(id));

  const clear = () => setItems([]);

  const value = useMemo(
    () => ({
      items,
      addItem,
      removeItem,
      toggleItem,
      isInWishlist,
      clear,
    }),
    [items]
  );

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
}
